import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Pencil, 
  Plus, 
  FileText, 
  Search,
  Layers, 
  Table, 
  LayoutGrid, 
  Laptop, 
  Box, 
  Server, 
  Monitor, 
  ShieldCheck, 
  MapPin, 
  AlertTriangle, 
  CheckCircle2, 
  Hash,
  User,
  Code2,
  Trash2,
  UserMinus
} from 'lucide-react';
import { useAssetCatalogStore } from '../../../store/assetCatalogStore';
import { useAssetStore, Asset, ownershipLabel } from '../../../store/assetStore';
import { useTicketStore } from '../../../store/ticketStore';
import { ITSMAssetDetailView } from './AssetDetailView';
import { DataTable, type Column } from '../../../components/DataTable';
import { Pagination } from '../../../components/Pagination';
import { UserAvatar } from '../../../components/UserAvatar';
import { CopyButton } from '../../../components/CopyButton';
import { cn } from '../../../lib/utils';
import toast from 'react-hot-toast';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/Dialog';
import { useAssetGrant } from '../requests/requestUi';


export function AssetRegistry() {
  const { assets, isLoading, error, fetchAssets, deleteAsset, returnAsset } = useAssetStore();
  const canDelete = useAssetGrant('Assets', 'Delete');
  const canEdit = useAssetGrant('Assets', 'Edit');
  const { categories: categoryRows, types: typeRows, fetchCategories, fetchTypes } = useAssetCatalogStore();
  const { tickets, fetchTickets } = useTicketStore();
  const navigate = useNavigate();

  // Deduplicate option values case-insensitively (keeps the first-seen casing as the label)
  // so e.g. "iPhone" from the catalog and "IPhone" on an asset collapse into one option.
  const dedupe = (values: (string | null | undefined)[]) => {
    const map = new Map<string, string>();
    for (const v of values) {
      const s = (v ?? '').trim();
      if (s && !map.has(s.toLowerCase())) map.set(s.toLowerCase(), s);
    }
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
  };
  // Options come from the catalog (Types/Categories) plus any value assets actually use.
  const TYPES = ['All', ...dedupe([...typeRows.map((t) => t.name), ...assets.map((a) => a.type)])];
  const CATEGORIES = ['All', ...dedupe([...categoryRows.map((c) => c.name), ...assets.map((a) => a.category)])];
  const STATUSES = ['All', ...dedupe(assets.map((a) => a.status))];

  // View Mode: 'card' (default) or 'table'
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [catFilter, setCatFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [ownershipFilter, setOwnershipFilter] = useState('All');   // All | owned | rental
  const [assignmentFilter, setAssignmentFilter] = useState('All'); // All | assigned | unassigned
  const [issuesFilter, setIssuesFilter] = useState('All');         // All | yes | no
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);

  const filtersActive =
    !!search || typeFilter !== 'All' || catFilter !== 'All' || statusFilter !== 'All' ||
    ownershipFilter !== 'All' || assignmentFilter !== 'All' || issuesFilter !== 'All';

  const resetFilters = () => {
    setSearch(''); setTypeFilter('All'); setCatFilter('All'); setStatusFilter('All');
    setOwnershipFilter('All'); setAssignmentFilter('All'); setIssuesFilter('All');
  };

  const selectClass =
    'h-9 rounded-lg border border-input bg-card px-3 text-xs font-medium text-foreground shadow-2xs outline-none focus:ring-1 focus:ring-ring cursor-pointer';

  // Delete + Unassign (return to stock) confirmation dialogs.
  const [deletingAsset, setDeletingAsset] = useState<Asset | null>(null);
  const [unassigningAsset, setUnassigningAsset] = useState<Asset | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  const confirmDelete = async () => {
    if (!deletingAsset) return;
    setActionBusy(true);
    try {
      await deleteAsset(deletingAsset.id);
      toast.success(`${deletingAsset.asset_code || 'Asset'} deleted`);
      setDeletingAsset(null);
    } catch (e: any) {
      toast.error(e?.message || 'Could not delete the asset');
    } finally {
      setActionBusy(false);
    }
  };

  const confirmUnassign = async () => {
    if (!unassigningAsset) return;
    setActionBusy(true);
    try {
      await returnAsset(unassigningAsset.id);
      toast.success(`${unassigningAsset.asset_code || 'Asset'} returned to stock`);
      setUnassigningAsset(null);
    } catch (e: any) {
      toast.error(e?.message || 'Could not unassign the asset');
    } finally {
      setActionBusy(false);
    }
  };

  useEffect(() => {
    fetchAssets();
    fetchTickets();
    fetchCategories();
    fetchTypes();
  }, [fetchAssets, fetchTickets, fetchCategories, fetchTypes]);

  // Reset page when any filter or the search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, typeFilter, catFilter, statusFilter, ownershipFilter, assignmentFilter, issuesFilter, pageSize, viewMode]);

  // Assets that currently have at least one open (not resolved/closed) support ticket.
  const openIssueKeys = useMemo(() => {
    const ids = new Set<string>();
    const tags = new Set<string>();
    for (const t of tickets) {
      if (t.status === 'resolved' || t.status === 'closed') continue;
      if (t.assetId != null) ids.add(String(t.assetId));
      if (t.assetTag) tags.add(t.assetTag.toLowerCase());
    }
    return { ids, tags };
  }, [tickets]);
  const hasOpenIssue = (a: Asset) =>
    openIssueKeys.ids.has(String(a.id)) ||
    (!!a.asset_code && openIssueKeys.tags.has(a.asset_code.toLowerCase()));

  const filteredAssets = useMemo(() => {
    const term = search.trim().toLowerCase();
    const eq = (v: string | null | undefined, sel: string) => (v || '').toLowerCase() === sel.toLowerCase();
    return assets.filter((a) => {
      // Search across the common identifying fields (asset id, assignee, serial, model, name, etc.)
      const matchSearch = !term || [
        a.asset_code, a.name, a.model, a.serial_number, a.assignedToName,
        a.manufacturer, a.type, a.category, a.department, a.location,
      ].some((f) => f?.toLowerCase().includes(term));

      const matchType = typeFilter === 'All' || eq(a.type, typeFilter);
      const matchCat = catFilter === 'All' || eq(a.category, catFilter);
      const matchStatus = statusFilter === 'All' || eq(a.status, statusFilter);
      const matchOwnership = ownershipFilter === 'All' || eq(a.ownership, ownershipFilter);

      const isAssigned = !!(a.assignedTo || a.assignedToName);
      const matchAssignment =
        assignmentFilter === 'All' || (assignmentFilter === 'assigned' ? isAssigned : !isAssigned);

      const matchIssues =
        issuesFilter === 'All' || (issuesFilter === 'yes' ? hasOpenIssue(a) : !hasOpenIssue(a));

      // AND / narrowing: an asset must satisfy every active filter.
      return matchSearch && matchType && matchCat && matchStatus && matchOwnership && matchAssignment && matchIssues;
    });
  }, [assets, search, typeFilter, catFilter, statusFilter, ownershipFilter, assignmentFilter, issuesFilter, openIssueKeys]);

  const totalItems = filteredAssets.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const validPage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (validPage - 1) * pageSize;
  const paginatedAssets = filteredAssets.slice(startIndex, startIndex + pageSize);

  // Helper function for asset icon by category / type
  const getAssetCategoryIcon = (type?: string, category?: string) => {
    const t = (type || category || '').toLowerCase();
    if (t.includes('software') || t.includes('application') || t.includes('code') || t.includes('microservice')) return Code2;
    if (t.includes('laptop') || t.includes('macbook') || t.includes('thinkpad') || t.includes('dell')) return Laptop;
    if (t.includes('server') || t.includes('cloud')) return Server;
    if (t.includes('monitor') || t.includes('display')) return Monitor;
    if (t.includes('license')) return ShieldCheck;
    return Box;
  };

  // Status badge styling helper
  const getStatusStyle = (status?: string) => {
    const s = (status || '').toLowerCase().trim();
    if (s.includes('in stock') || s.includes('in_stock') || s.includes('available')) {
      return {
        badge: 'bg-info/15 text-info-strong border-info/30',
        dot: 'bg-info',
        borderAccent: 'border-l-sky-500',
      };
    }
    if (s.includes('assigned') || s.includes('active') || s.includes('in use') || s.includes('in_use')) {
      return {
        badge: 'bg-success/15 text-success-strong border-success/30',
        dot: 'bg-success',
        borderAccent: 'border-l-emerald-500',
      };
    }
    if (s.includes('maintenance') || s.includes('repair') || s.includes('hold')) {
      return {
        badge: 'bg-warning/15 text-warning-strong border-warning/30',
        dot: 'bg-warning',
        borderAccent: 'border-l-amber-500',
      };
    }
    if (s.includes('lost') || s.includes('damaged')) {
      return {
        badge: 'bg-destructive/15 text-destructive-strong border-destructive/30',
        dot: 'bg-destructive',
        borderAccent: 'border-l-rose-500',
      };
    }
    if (s.includes('retired') || s.includes('disposed')) {
      return {
        badge: 'bg-muted text-muted-foreground border-border',
        dot: 'bg-muted-foreground',
        borderAccent: 'border-l-slate-400',
      };
    }
    return {
      badge: 'bg-secondary text-secondary-foreground border-border',
      dot: 'bg-muted-foreground',
      borderAccent: 'border-l-border',
    };
  };

  // Columns definition for DataTable row columns method
  const columns = useMemo<Column<Asset>[]>(() => [
    {
      id: 'sNo',
      header: 'S.No',
      width: '60px',
      cell: (_row) => {
        const index = paginatedAssets.indexOf(_row);
        return <span className="font-mono text-muted-foreground">{startIndex + (index >= 0 ? index : 0) + 1}</span>;
      },
    },
    {
      id: 'assetCode',
      header: 'Asset_ID',
      width: '130px',
      sortable: true,
      sortAccessor: (a) => a.asset_code,
      cell: (asset) => (
        <div className="flex items-center gap-1.5 font-mono font-semibold text-primary group-hover:underline">
          {asset.images && asset.images[0] && (
            <img
              src={asset.images[0]}
              alt=""
              className="size-6 rounded border border-border object-cover shrink-0"
            />
          )}
          <span>{asset.asset_code}</span>
          <CopyButton value={asset.asset_code} label="Asset ID" />
        </div>
      ),
    },
    {
      id: 'assignedPerson',
      header: 'Assigned_Person',
      width: '180px',
      sortable: true,
      sortAccessor: (a) => a.assignedToName || '',
      cell: (asset) => (
        <div className="flex items-center gap-2 font-medium text-foreground">
          {asset.assignedToName ? (
            <>
              <UserAvatar name={asset.assignedToName} size="xs" />
              <span className="truncate">{asset.assignedToName}</span>
            </>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      width: '110px',
      sortable: true,
      sortAccessor: (a) => a.type || '',
      cell: (asset) => (
        <span className="text-muted-foreground">{asset.type || '—'}</span>
      ),
    },
    {
      id: 'category',
      header: 'Category',
      width: '120px',
      sortable: true,
      sortAccessor: (a) => a.category || '',
      cell: (asset) => (
        <span className="text-muted-foreground">{asset.category || '—'}</span>
      ),
    },
    {
      id: 'ownership',
      header: 'Ownership',
      width: '110px',
      sortable: true,
      sortAccessor: (a) => a.ownership || '',
      cell: (asset) =>
        asset.ownership ? (
          <span
            className={cn(
              'inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold border',
              asset.ownership === 'rental'
                ? 'bg-info/10 text-info-strong border-info/20'
                : 'bg-secondary text-secondary-foreground border-border'
            )}
          >
            {ownershipLabel(asset.ownership)}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '150px',
      sortable: true,
      sortAccessor: (a) => a.status,
      cell: (asset) => {
        const openCount = tickets.filter(
          (t) =>
            (t.assetId === asset.id ||
              (asset.id && String(t.assetId) === String(asset.id)) ||
              (asset.asset_code && t.assetTag?.toLowerCase() === asset.asset_code?.toLowerCase())) &&
            t.status !== 'resolved' &&
            t.status !== 'closed'
        ).length;
        const getStatusBadgeColor = (status?: string) => {
          const s = (status || '').toLowerCase().trim();
          if (s.includes('in stock') || s.includes('available')) {
            return 'bg-info text-info-foreground shadow-2xs';
          }
          if (s.includes('assigned') || s.includes('in use') || s.includes('active')) {
            return 'bg-success text-success-foreground shadow-2xs';
          }
          if (s.includes('maintenance') || s.includes('repair') || s.includes('service')) {
            return 'bg-warning text-warning-foreground shadow-2xs';
          }
          if (s.includes('retired')) {
            return 'bg-slate-500 text-white shadow-2xs';
          }
          if (s.includes('lost') || s.includes('damaged')) {
            return 'bg-destructive text-destructive-foreground shadow-2xs';
          }
          if (s.includes('disposed')) {
            return 'bg-slate-600 text-white shadow-2xs';
          }
          return 'bg-secondary text-secondary-foreground shadow-2xs';
        };

        return (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className={cn(
                'inline-flex items-center justify-center rounded px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap',
                getStatusBadgeColor(asset.status)
              )}
            >
              {asset.status || 'Active'}
            </span>
            {openCount > 0 && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/support/tickets?assetTag=${asset.asset_code}`);
                }}
                className="inline-flex items-center gap-1 text-xs text-warning-strong bg-warning/10 hover:bg-warning/10 px-1.5 py-0.5 rounded font-mono font-semibold border border-warning/40 transition-colors cursor-pointer"
                title="Click to view open support tickets"
              >
                ⚠️ {openCount} {openCount === 1 ? 'ticket' : 'tickets'}
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'serialNumber',
      header: 'Serial No.',
      width: '160px',
      sortable: true,
      sortAccessor: (a) => a.serial_number || (a.specifications?.version ? `v${a.specifications.version}` : ''),
      cell: (asset) => (
        <span className="inline-flex items-center gap-1.5 font-mono text-foreground">
          {asset.serial_number || (asset.specifications?.version ? `v${asset.specifications.version}` : '—')}
          {asset.serial_number && <CopyButton value={asset.serial_number} label="Serial number" />}
        </span>
      ),
    },
    {
      id: 'model',
      header: 'Model',
      sortable: true,
      sortAccessor: (a) => a.name || a.model || '',
      cell: (asset) => (
        <div>
          <span className="font-semibold text-foreground">{asset.name || asset.model}</span>
          {asset.model && asset.name !== asset.model && (
            <span className="text-muted-foreground ml-1.5 font-mono text-xs">({asset.model})</span>
          )}
          {((asset.invoices && asset.invoices.length > 0) || asset.invoiceNumber) && (
            <span className="ml-2 inline-flex items-center gap-1 text-xs text-success bg-success/10 px-1.5 py-0.5 rounded font-mono font-semibold">
              📄 Invoiced
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'issues',
      header: 'Issues?',
      width: '80px',
      align: 'center',
      cell: (asset) => (
        <span className="font-medium text-foreground">{
          tickets.some((t) => (String(t.assetId) === String(asset.id) || (asset.asset_code && t.assetTag?.toLowerCase() === asset.asset_code.toLowerCase())) && t.status !== 'resolved' && t.status !== 'closed') ? 'Yes' : 'No'
        }</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: '110px',
      align: 'center',
      cell: (asset) => {
        const assigned = !!(asset.assignedTo || asset.assignedToName);
        return (
          <div className="flex items-center justify-center gap-0.5">
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/assets/registry/${encodeURIComponent(asset.asset_code || asset.id)}?edit=true`);
              }}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors cursor-pointer"
              title="Edit asset"
            >
              <Pencil className="size-3.5" />
            </button>
            {canEdit && assigned && (
              <button
                onClick={(e) => { e.stopPropagation(); setUnassigningAsset(asset); }}
                className="p-1.5 text-muted-foreground hover:text-warning-strong hover:bg-warning/10 rounded-md transition-colors cursor-pointer"
                title="Unassign (return to stock)"
              >
                <UserMinus className="size-3.5" />
              </button>
            )}
            {canDelete && (
              <button
                onClick={(e) => { e.stopPropagation(); setDeletingAsset(asset); }}
                className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-colors cursor-pointer"
                title="Delete asset"
              >
                <Trash2 className="size-3.5" />
              </button>
            )}
          </div>
        );
      },
    },
  ], [startIndex, paginatedAssets, tickets, navigate, canEdit, canDelete]);

  return (
    <div className="p-4 space-y-4 bg-background min-h-full text-foreground text-xs font-sans flex flex-col justify-between">
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <div className="space-y-4">
        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* HEADER SECTION                                                 */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* HEADER SECTION CARD */}
        <div className="p-4 rounded-xl border border-border bg-card shadow-xs flex flex-col sm:flex-row justify-between sm:items-center gap-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="size-9 rounded-lg bg-primary/10 border border-primary/20 text-primary flex items-center justify-center font-bold shrink-0">
              <Layers className="size-4.5" />
            </div>
            <div className="space-y-0.5 min-w-0">
              <h1 className="tracking-tight text-foreground font-display text-page-title font-semibold truncate">
                Asset Management
              </h1>
              <p className="text-xs text-muted-foreground leading-relaxed truncate max-w-4xl">
                {catFilter === 'All' ? 'Hardware & Software Inventory' : `${catFilter} Inventory`} ({totalItems} total tracked assets)
              </p>
            </div>
          </div>

          {/* Top Right Action Button: Add Asset */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => navigate('/assets/new')}
              className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-primary px-4 text-xs font-semibold text-primary-foreground shadow transition-colors hover:bg-primary/90 cursor-pointer"
            >
              <Plus className="size-4" />
              Add Asset
            </button>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* FILTER TOOLBAR: SEARCH, CATEGORY DROPDOWN & VIEW MODE SWITCHER */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            {/* Search Input */}
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder="Search by ID, person, serial, model…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 w-full rounded-lg border border-input bg-card pl-8 pr-3 text-xs text-foreground shadow-2xs placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-ring"
              />
            </div>

            {/* Type */}
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={selectClass} title="Filter by type">
              {TYPES.map((t) => (
                <option key={t} value={t}>{t === 'All' ? 'All Types' : t}</option>
              ))}
            </select>

            {/* Category */}
            <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className={selectClass} title="Filter by category">
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c === 'All' ? 'All Categories' : c}</option>
              ))}
            </select>

            {/* Status */}
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectClass} title="Filter by status">
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s === 'All' ? 'All Statuses' : s}</option>
              ))}
            </select>

            {/* Ownership */}
            <select value={ownershipFilter} onChange={(e) => setOwnershipFilter(e.target.value)} className={selectClass} title="Filter by ownership">
              <option value="All">All Ownership</option>
              <option value="owned">Owned</option>
              <option value="rental">Rental</option>
            </select>

            {/* Assignment */}
            <select value={assignmentFilter} onChange={(e) => setAssignmentFilter(e.target.value)} className={selectClass} title="Filter by assignment">
              <option value="All">All Assignment</option>
              <option value="assigned">Assigned</option>
              <option value="unassigned">Unassigned</option>
            </select>

            {/* Issues */}
            <select value={issuesFilter} onChange={(e) => setIssuesFilter(e.target.value)} className={selectClass} title="Filter by open issues">
              <option value="All">All (issues)</option>
              <option value="yes">Has issues</option>
              <option value="no">No issues</option>
            </select>

            {filtersActive && (
              <button
                type="button"
                onClick={resetFilters}
                className="h-9 px-3 rounded-lg border border-border bg-card text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-accent shadow-2xs cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>

          {/* View Mode Switcher: Cards vs Table */}
          <div className="flex items-center rounded-lg border border-border bg-card p-0.5 shadow-2xs shrink-0 self-end sm:self-auto">
            <button
              onClick={() => setViewMode('card')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors cursor-pointer',
                viewMode === 'card'
                  ? 'bg-primary text-primary-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              )}
              title="Card View (1 Card per Row)"
            >
              <LayoutGrid className="size-3.5" />
              <span>Cards</span>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors cursor-pointer',
                viewMode === 'table'
                  ? 'bg-primary text-primary-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              )}
              title="Table View (Row Columns)"
            >
              <Table className="size-3.5" />
              <span>Table</span>
            </button>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* MAIN VIEW CONTENT: 1 CARD PER ROW (DEFAULT) OR DATA TABLE      */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {viewMode === 'card' ? (
          /* ───────────────────────────────────────────────────────────── */
          /* CARD VIEW: 1 CARD PER ROW (HORIZONTAL WIDE LAYOUT)            */
          /* ───────────────────────────────────────────────────────────── */
          <div className="space-y-4">
            {isLoading && assets.length === 0 ? (
              <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground shadow-xs">
                <div className="flex items-center justify-center gap-2">
                  <span className="size-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></span>
                  Loading asset registry records...
                </div>
              </div>
            ) : filteredAssets.length === 0 ? (
              <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground shadow-xs">
                <FileText className="mx-auto size-8 text-muted-foreground/50 mb-2" />
                No assets found matching the search criteria.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {paginatedAssets.map((asset, index) => {
                  const statusStyle = getStatusStyle(asset.status);
                  const AssetIcon = getAssetCategoryIcon(asset.type, asset.category ?? undefined);

                  const openTicketsCount = tickets.filter(
                    (t) =>
                      (t.assetId === asset.id ||
                        (asset.id && String(t.assetId) === String(asset.id)) ||
                        (asset.asset_code && t.assetTag?.toLowerCase() === asset.asset_code?.toLowerCase())) &&
                      t.status !== 'resolved' &&
                      t.status !== 'closed'
                  ).length;

                  const isAssigned = !!(asset.assignedTo || asset.assignedToName);
                  return (
                    <div
                      key={asset.id}
                      onClick={() => navigate(`/assets/registry/${encodeURIComponent(asset.asset_code || asset.id)}`)}
                      className={cn(
                        'group relative flex flex-col rounded-lg border border-border bg-card p-3 shadow-xs hover:shadow-md hover:border-primary/50 transition-all cursor-pointer border-t-4',
                        statusStyle.borderAccent
                      )}
                    >
                      {/* Top row: index + hover actions */}
                      <div className="flex items-start justify-between gap-2 h-5">
                        <span className="text-[10px] font-mono text-muted-foreground">#{startIndex + index + 1}</span>
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/assets/registry/${encodeURIComponent(asset.asset_code || asset.id)}?edit=true`); }}
                            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-accent"
                            title="Edit asset"
                          >
                            <Pencil className="size-3.5" />
                          </button>
                          {canEdit && isAssigned && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setUnassigningAsset(asset); }}
                              className="p-1 rounded text-muted-foreground hover:text-warning-strong hover:bg-warning/10"
                              title="Unassign (return to stock)"
                            >
                              <UserMinus className="size-3.5" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setDeletingAsset(asset); }}
                              className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                              title="Delete asset"
                            >
                              <Trash2 className="size-3.5" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Thumbnail */}
                      <div className="mx-auto mt-1 mb-2 size-16 rounded-lg border border-border bg-muted/30 flex items-center justify-center overflow-hidden">
                        {asset.images && asset.images[0] ? (
                          <img src={asset.images[0]} alt="" className="size-full object-cover" />
                        ) : (
                          <AssetIcon className="size-7 text-muted-foreground" />
                        )}
                      </div>

                      {/* Name + code */}
                      <div className="text-center min-w-0">
                        <h3
                          className="truncate font-display text-card-title text-foreground group-hover:text-primary transition-colors"
                          title={asset.name || asset.model || 'Untitled Asset'}
                        >
                          {asset.name || asset.model || 'Untitled Asset'}
                        </h3>
                        <span className="inline-flex items-center gap-1 font-mono text-[11px] font-semibold text-primary">
                          {asset.asset_code}
                          <CopyButton value={asset.asset_code} label="Asset ID" />
                        </span>
                      </div>

                      {/* Type / Category badges */}
                      <div className="mt-1.5 flex flex-wrap justify-center gap-1">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-muted text-muted-foreground border border-border/70">
                          {asset.type || 'Asset'}
                        </span>
                        {asset.category && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-secondary text-secondary-foreground border border-border/70">
                            {asset.category}
                          </span>
                        )}
                      </div>

                      {/* Status + ownership */}
                      <div className="mt-1.5 flex flex-wrap justify-center gap-1">
                        <span className={cn('inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border', statusStyle.badge)}>
                          <span className={cn('size-1.5 rounded-full', statusStyle.dot)} />
                          {asset.status || 'Active'}
                        </span>
                        {asset.ownership && (
                          <span
                            className={cn(
                              'px-1.5 py-0.5 rounded text-[10px] font-semibold border',
                              asset.ownership === 'rental'
                                ? 'bg-info/10 text-info-strong border-info/20'
                                : 'bg-secondary text-secondary-foreground border-border'
                            )}
                          >
                            {ownershipLabel(asset.ownership)}
                          </span>
                        )}
                      </div>

                      {/* Assignee */}
                      <div className="mt-2 flex items-center justify-center gap-1.5 text-xs">
                        {asset.assignedToName ? (
                          <>
                            <UserAvatar name={asset.assignedToName} size="xs" />
                            <span className="truncate font-medium text-foreground max-w-[9rem]" title={asset.assignedToName}>
                              {asset.assignedToName}
                            </span>
                          </>
                        ) : (
                          <span className="text-muted-foreground">Unassigned</span>
                        )}
                      </div>

                      {/* Footer: serial + issues */}
                      <div className="mt-2 pt-2 border-t border-border/60 flex items-center justify-between text-[11px] text-muted-foreground">
                        <span className="font-mono truncate max-w-[55%]" title={asset.serial_number || ''}>
                          {asset.serial_number ? `SN ${asset.serial_number}` : 'SN —'}
                        </span>
                        {openTicketsCount > 0 ? (
                          <span
                            onClick={(e) => { e.stopPropagation(); navigate(`/support/tickets?assetTag=${asset.asset_code}`); }}
                            className="inline-flex items-center gap-1 text-warning-strong bg-warning/10 px-1.5 py-0.5 rounded font-semibold border border-warning/40"
                            title="View open support tickets"
                          >
                            <AlertTriangle className="size-3" /> {openTicketsCount}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1">
                            <CheckCircle2 className="size-3 text-success" /> OK
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* CARD VIEW PAGINATION TOOLBAR */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 rounded-lg border border-border bg-card px-5 py-3.5 text-xs text-muted-foreground shadow-xs">
              <div className="flex items-center gap-2">
                <span>Assets per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="h-8 rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring font-medium cursor-pointer"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={40}>40</option>
                  <option value={80}>80</option>
                </select>
              </div>

              <Pagination
                page={validPage}
                pageCount={totalPages}
                onPageChange={setCurrentPage}
                total={totalItems}
                pageSize={pageSize}
              />
            </div>
          </div>
        ) : (
          /* ───────────────────────────────────────────────────────────── */
          /* TABLE VIEW: ROW COLUMNS METHOD (DataTable Component)          */
          /* ───────────────────────────────────────────────────────────── */
          <div className="rounded-lg border border-border bg-card shadow-xs overflow-hidden">
            <DataTable
              columns={columns}
              data={paginatedAssets}
              keyExtractor={(a) => a.id}
              isLoading={isLoading && assets.length === 0}
              onRowClick={(asset) => {
                navigate(`/assets/registry/${encodeURIComponent(asset.asset_code || asset.id)}`);
              }}
              footer={
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-border px-5 py-3.5 bg-card text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <span>Rows per page:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="h-8 rounded-md border border-input bg-background px-2.5 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring font-medium cursor-pointer"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  <Pagination
                    page={validPage}
                    pageCount={totalPages}
                    onPageChange={setCurrentPage}
                    total={totalItems}
                    pageSize={pageSize}
                  />
                </div>
              }
            />
          </div>
        )}
      </div>

      {/* Delete asset confirmation */}
      <Dialog open={!!deletingAsset} onOpenChange={(o) => !o && setDeletingAsset(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete {deletingAsset?.asset_code || 'asset'}?</DialogTitle>
            <DialogDescription>
              {deletingAsset?.name ? `"${deletingAsset.name}" ` : ''}will be removed from the registry. This can’t be undone from here.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setDeletingAsset(null)}
              disabled={actionBusy}
              className="h-9 px-4 rounded-lg border border-border bg-card text-xs font-semibold text-foreground hover:bg-accent disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={actionBusy}
              className="h-9 px-4 rounded-lg bg-destructive text-xs font-semibold text-destructive-foreground hover:bg-destructive/90 disabled:opacity-60"
            >
              {actionBusy ? 'Deleting…' : 'Delete'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unassign (return to stock) confirmation */}
      <Dialog open={!!unassigningAsset} onOpenChange={(o) => !o && setUnassigningAsset(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Unassign {unassigningAsset?.asset_code || 'asset'}?</DialogTitle>
            <DialogDescription>
              This returns the asset to stock{unassigningAsset?.assignedToName ? ` from ${unassigningAsset.assignedToName}` : ''}. The asset itself is kept.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setUnassigningAsset(null)}
              disabled={actionBusy}
              className="h-9 px-4 rounded-lg border border-border bg-card text-xs font-semibold text-foreground hover:bg-accent disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmUnassign}
              disabled={actionBusy}
              className="h-9 px-4 rounded-lg bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {actionBusy ? 'Returning…' : 'Unassign'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
